import React, { useState, useEffect, useRef } from 'react';

interface PaymentComponentProps {
  onBack: () => void;
  user: any;
}

export const PaymentComponent: React.FC<PaymentComponentProps> = ({ onBack, user }) => {
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [paymentCode, setPaymentCode] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const paymentFormRef = useRef<HTMLDivElement>(null);

  const tiers = [
    { id: "TIER1", name: 'Tier 1 - Starter' },
    { id: "TIER2", name: 'Tier 2 - Growth' },
    { id: "TIER3", name: 'Tier 3 - Premium' }
  ];
  
  const selectedTierInfo = tiers.find(tier => tier.id === user?.tier);

  useEffect(() => {
    if (selectedPayment && paymentFormRef.current) {
      paymentFormRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedPayment]);

  const handlePaymentSubmit = async () => {
    if (!paymentCode) {
      setMessage('Please enter the payment code');
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsSubmitting(true);

    if (selectedPayment === 'bank-transfer') {
      try {
        const response = await fetch(`https://api.regenphd.com/regenpod-partner-payment-stripe/${user?._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: paymentCode })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          setMessage(data.error || 'Payment setup failed');
          setMessageType('error');
        } else {
          setMessage('Invoice sent to your email with bank transfer details and Stripe payment link');
          setMessageType('success');
        }
      } catch (error) {
        setMessage('Failed to send invoice');
        setMessageType('error');
      }
      setTimeout(() => setMessage(''), 5000);
    } else {
      try {
        const response = await fetch(`https://api.regenphd.com/regenpod-partner-payment-confirm/${user?._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            code: paymentCode, 
            paymentOption: selectedPayment 
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          setMessage(data.error || 'Payment confirmation failed');
          setMessageType('error');
        } else {
          setIsPaymentConfirmed(true);
        }
        setTimeout(() => setMessage(''), 5000);
      } catch (error) {
        setMessage('Failed to confirm payment');
        setMessageType('error');
        setTimeout(() => setMessage(''), 5000);
      }
    }
    
    setIsSubmitting(false);
  };

  // If payment is already confirmed, show success message
  if (user?.paymentReceived || isPaymentConfirmed) {
    return (
      <div className="payment-section">
        <div className="payment-header">
          <h2 className="section-title">Payment Confirmed</h2>
          {user?.name && <p className="user-name">Welcome, {user.name}! {selectedTierInfo && <p className="tier-small">({selectedTierInfo.name})</p>}</p>}
        </div>
        <div className="payment-success">
          <div className="success-icon">✓</div>
          <h3>Payment Successfully Received!</h3>
          <p>Your £2,500 activation deposit has been confirmed.</p>

          <p>We will contact you shortly to schedule delivery and installation.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-section">
      <div className="payment-header">
        <button onClick={onBack} className="back-btn">← Back</button>
        <h2 className="section-title">Payment Options</h2>
        {user?.name && <p className="user-name">Welcome, {user.name}! {selectedTierInfo && <p className="tier-small">({selectedTierInfo.name})</p>}</p>}
        <p className="payment-description">Choose your preferred payment method for the £2,500 activation deposit</p>

      </div>

      <div className="payment-options">
        <div 
          className={`payment-option ${selectedPayment === 'cash' ? 'selected' : ''}`}
          onClick={() => setSelectedPayment('cash')}
        >
          <h3>Pay via Cash</h3>
          <p>Cash payment option</p>
        </div>

        <div 
          className={`payment-option ${selectedPayment === 'card' ? 'selected' : ''}`}
          onClick={() => setSelectedPayment('card')}
        >
          <h3>Pay via Card</h3>
          <p>Card payment option</p>
        </div>

        <div 
          className={`payment-option ${selectedPayment === 'bank-transfer' ? 'selected' : ''}`}
          onClick={() => setSelectedPayment('bank-transfer')}
        >
          <h3>Pay via Bank Transfer/Stripe</h3>
          <p>Invoice will be sent with payment options</p>
        </div>
      </div>

      {selectedPayment && (
        <div className="payment-form" ref={paymentFormRef}>
          {selectedPayment === 'bank-transfer' && (
            <div className="bank-transfer-info">
              <p>An invoice with bank transfer details and Stripe payment link will be sent to <span className="font-semibold">{user?.email}</span></p>
            </div>
          )}

          <div className="code-input">
            <input
              type="text"
              placeholder="Enter payment code *"
              value={paymentCode}
              onChange={(e) => setPaymentCode(e.target.value)}
              className="payment-code-input"
            />
          </div>

          <button 
            onClick={handlePaymentSubmit}
            className="submit-payment-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Payment'}
          </button>
        </div>
      )}

      {message && (
        <div className={`payment-message ${messageType}`}>
          {message}
        </div>
      )}
    </div>
  );
};